import { type MaybeRefOrGetter, computed, toValue } from 'vue';

import { FEATURE_FLAG } from '@/constants/feature_flags';
import { useFeatureFlagStore, usePartnersStore } from '@/stores';
import type { Partner, PartnerTrimmed } from '@/types/partners';

type MaybePartner = Partner | PartnerTrimmed | undefined;

export default function useDeprecatedManagedOfflinePartner(
  partner: MaybeRefOrGetter<MaybePartner>,
) {
  const { hasFeatureFlag } = useFeatureFlagStore();
  const partnersStore = usePartnersStore();

  const isDeprecatedMop = computed(() => {
    const org = toValue(partner);
    return (
      hasFeatureFlag(FEATURE_FLAG.TEMP_DEPRECATED_MOP) &&
      !!org?.virtual_partner &&
      !!org?.is_mop
    );
  });

  /**
   * `odp_org_id` only tells us an Open Data Partner org shares this MOP's
   * domain, which the API answers platform-wide. Replacing is only impossible
   * once that org is one of our own partners, so the id has to be looked up in
   * our partner list rather than trusted on its own.
   */
  const hasExistingOpenDataPartner = computed(() => {
    if (!isDeprecatedMop.value) return false;
    const openDataPartnerId = toValue(partner)?.odp_org_id;
    if (openDataPartnerId == null) return false;
    return !!partnersStore.partnerOrgsLookup[openDataPartnerId];
  });

  return {
    hasExistingOpenDataPartner,
    isDeprecatedMop,
  };
}
